import { PREVIEW_NS, type PreviewFocusMessage, type PreviewRect } from "@innoclouds/ui";
import { postToPreview } from "../mode";
import { find, first, hasText, inView, sleep, tap, visibleFirst, waitFor } from "./dom";
import { dismissStart } from "./menu";
import { ABSENT, type Place, placesFor } from "./places";

const TRACK = 300;

const OPENERS: Record<string, [prefix: string, opener: string][]> = {
	product_card: [
		["modifiers_", "settings_button"],
		["info_", "info_button"],
	],
	cart: [["comment_", "comment_button"]],
};

const pick = (place: Place, { screen, element, demo }: PreviewFocusMessage) => {
	const instances = place.scopes().flatMap((scope) => find(screen, element, scope));
	const subject = demo.subjects[0];
	const own = subject ? instances.filter((el) => hasText(el.closest('[data-el="card"], [data-el="line"]') ?? el, subject)) : [];
	return visibleFirst(own.length ? own : instances);
};

let session = 0;

const report = (status: "ok" | "missing" | "error", rect: PreviewRect | null, place: Place | null, places?: Place[]) =>
	postToPreview({
		ns: PREVIEW_NS,
		type: "focus-rect",
		status,
		rect,
		label: place?.title ?? null,
		place: place && { id: place.id, title: place.title },
		places: places?.map(({ id, title }) => ({ id, title })),
	});

export const focusTile = async (focus: PreviewFocusMessage) => {
	const current = ++session;
	const places = placesFor(focus);
	const place = places.find(({ id }) => id === focus.place) ?? places[0];
	let announce = focus.place ? undefined : places;
	if (!place) return report("missing", null, null, announce);

	await dismissStart();
	const entered = place.scopes().length ? true : await place.enter(focus.demo);
	if (current !== session) return;
	if (entered === ABSENT) return report("missing", null, place, announce);
	if (place.seek && place.scopes().length) await place.seek(focus.demo);
	if (current !== session) return;
	if (!place.scopes().length) return report("error", null, place, announce);

	let target = await waitFor(() => pick(place, focus), 2500);
	const opener = OPENERS[focus.screen]?.find(([prefix]) => focus.element.startsWith(prefix))?.[1];
	if (!target && opener) {
		tap(first(focus.screen, opener));
		target = await waitFor(() => pick(place, focus), 2500);
	}
	if (current !== session) return;
	if (!target) return report("missing", null, place, announce);

	if (!inView(target)) target.scrollIntoView({ block: "center", inline: "center" });

	let last = "";
	while (current === session) {
		const el = pick(place, focus) ?? target;
		const { x, y, width, height } = el.getBoundingClientRect();
		const rect = { x, y, width, height };
		const key = JSON.stringify(rect);
		if (key !== last) {
			last = key;
			report("ok", rect, place, announce);
			announce = undefined;
		}
		await sleep(TRACK);
	}
};
